import { useState } from 'react'
import { getConfig } from '@/shared/config'
import { cn } from './primitives'

/**
 * The brand lockup, in the two forms the app needs.
 *
 * The SIPAY artwork is committed here because it belongs to this application.
 * The corporate logo is not: it is read from runtime config (see
 * AppConfig.companyLogoUrl) so the official file can be dropped in without a
 * rebuild, and so this repo never ships an approximation of a registered mark.
 */

/** The SIPAY mark on its own — the square glyph, no wordmark. Used wherever space is tight. */
export function SipayMark({ className }: { className?: string }) {
  return (
    <img
      src="/brand/sipay-mark.png"
      alt=""
      aria-hidden="true"
      className={cn('size-10 select-none object-contain', className)}
      draggable={false}
    />
  )
}

/** The full SIPAY lockup: mark plus wordmark. */
export function SipayLogo({ className }: { className?: string }) {
  return (
    <img
      src="/brand/sipay-logo.png"
      alt="SIPAY"
      className={cn('h-11 w-auto select-none object-contain', className)}
      draggable={false}
    />
  )
}

/**
 * The corporate logo, or nothing.
 *
 * Hiding on load failure is deliberate: until someone drops the official file
 * at the configured path the request 404s once and the element disappears,
 * which leaves a login screen that looks finished rather than one with a
 * broken-image icon where the company logo should be.
 */
export function CompanyLogo({ className }: { className?: string }) {
  const { companyLogoUrl, companyName } = getConfig()
  const [failed, setFailed] = useState(false)

  if (!companyLogoUrl || failed) return null

  return (
    <img
      src={companyLogoUrl}
      alt={companyName}
      className={cn('h-12 w-auto select-none object-contain', className)}
      draggable={false}
      onError={() => setFailed(true)}
    />
  )
}

/**
 * The loading indicator: the mark, breathing, inside a ring that sweeps.
 *
 * Two separate movements rather than one spinning logo. A rotating brand mark
 * reads as a loading GIF; keeping the mark on its own axis and moving only the
 * ring around it says "working" while the logo stays legible the whole time.
 */
export function BrandLoader({
  label,
  className,
}: {
  label?: string
  className?: string
}) {
  return (
    <div className={cn('flex flex-col items-center gap-3.5', className)}>
      <div className="relative grid size-16 place-items-center">
        <div
          aria-hidden="true"
          className="absolute inset-0 animate-sweep rounded-full border-2 border-gray-300/70 border-t-primary"
        />
        <SipayMark className="size-8 animate-breathe dark:brightness-0 dark:invert" />
      </div>
      {label && <p className="animate-fade text-gray-600">{label}</p>}
    </div>
  )
}

/**
 * Shown while a session is being restored, so a reload never flashes the login
 * page at someone who is already signed in.
 *
 * It lives here rather than beside the other primitives because it renders the
 * brand mark, and primitives.tsx must stay the leaf of the import graph.
 */
export function PageSpinner({ label = 'Memuat…' }: { label?: string }) {
  return (
    <div className="flex h-full items-center justify-center py-16" role="status" aria-live="polite">
      <BrandLoader label={label} />
    </div>
  )
}
